import { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SignUpForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    acceptTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password?.length >= 6) strength++;
    if (password?.length >= 10) strength++;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength++;
    if (/\d/?.test(password)) strength++;
    if (/[^a-zA-Z0-9]/?.test(password)) strength++;
    
    if (strength <= 2) return { strength: 33, label: 'Weak', color: 'bg-error' };
    if (strength <= 3) return { strength: 66, label: 'Medium', color: 'bg-warning' };
    return { strength: 100, label: 'Strong', color: 'bg-success' };
  };

  const passwordStrength = getPasswordStrength(formData?.password);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Please enter your full name';
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email or phone is required';
    } else if (formData?.email?.includes('@') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (!formData?.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    // UI mockup - simulate signup
    setTimeout(() => {
      const mockUserData = {
        id: 'user_' + Math.random()?.toString(36)?.substr(2, 9),
        name: formData?.fullName,
        email: formData?.email,
        provider: 'email',
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(formData?.fullName)}&background=FF6A00&color=fff`,
      };
      setIsLoading(false);
      onSuccess?.(mockUserData);
    }, 1500);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-[18px]">
      {/* Full Name Input */}
      <Input
        type="text"
        name="fullName"
        label="Full Name"
        placeholder="Juan Dela Cruz"
        value={formData?.fullName}
        onChange={handleChange}
        error={errors?.fullName}
        required
      />

      {/* Email/Phone Input */}
      <Input
        type="text"
        name="email"
        label="Email or Phone Number"
        placeholder="juan@example.com or 09171234567"
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        required
      />

      {/* Password Input with Strength Indicator */}
      <div className="space-y-[8px]">
        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            label="Password"
            placeholder="Create a strong password"
            value={formData?.password}
            onChange={handleChange}
            error={errors?.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-[12px] top-[38px] p-[4px] text-muted-foreground hover:text-foreground transition-smooth"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
          </button>
        </div>
        
        {/* Password Strength Bar */}
        {formData?.password && (
          <div className="space-y-[6px]">
            <div className="h-[6px] bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${passwordStrength?.color}`}
                style={{ width: `${passwordStrength?.strength}%` }}
              />
            </div>
            <p className="text-[12px] text-muted-foreground">
              Password strength: <span className="font-medium">{passwordStrength?.label}</span>
            </p>
          </div>
        )}
      </div>

      {/* Terms Acceptance */}
      <div className="space-y-[6px]">
        <label className="flex items-start gap-[8px] cursor-pointer group">
          <Input
            type="checkbox"
            name="acceptTerms"
            checked={formData?.acceptTerms}
            onChange={handleChange}
            className="mt-[2px]"
          />
          <span className="text-[14px] text-foreground leading-relaxed">
            I agree to the{' '}
            <a href="#" className="text-primary hover:underline font-medium">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="#" className="text-primary hover:underline font-medium">
              Privacy Policy
            </a>
          </span>
        </label>
        {errors?.acceptTerms && (
          <p className="text-[13px] text-destructive ml-[28px]">{errors?.acceptTerms}</p>
        )}
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        iconName="UserPlus"
        iconPosition="left"
      >
        {isLoading ? 'Creating account...' : 'Create Account'}
      </Button>

      {/* Helper Text */}
      <p className="text-[13px] text-muted-foreground text-center mt-[12px]">
        Simulan ang iyong journey! (Start your journey!)
      </p>
    </form>
  );
};

export default SignUpForm;